import {
  AbstractControl,
  FormGroupDirective,
  NgForm,
  ValidationErrors,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { IListado, IQueryParam } from 'modelos/src';
import { Observable, from, map, of, switchMap, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** Lo mínimo que necesita el chequeo de la entidad que busca. */
export interface EntidadIdentificable {
  _id?: string;
  idCliente?: string;
  cliente?: { nombre?: string };
}

export interface OpcionesChequeoIdentificacion<T> {
  /** Sustantivo para los mensajes: 'vehículo', 'alarma', 'cámara'… */
  entidad: string;
  /** Artículo del sustantivo. */
  articulo: 'un' | 'una';
  /** Campo del documento donde vive la identificación. */
  campo: string;
  /**
   * Control del form. Se pasa como getter y no se cachea en el validador
   * porque Angular no corre los validadores async si falla el sync: con el
   * campo vacío el control nunca quedaría asociado.
   */
  control: () => AbstractControl | null | undefined;
  /** Llamada HTTP que lista la entidad. */
  listar: (query: IQueryParam) => Promise<IListado<T>>;
  /** Cliente seleccionado en el form. */
  idCliente: () => string | undefined | null;
  /** Id de la entidad en edición, para excluirla del chequeo. */
  excluirId?: () => string | undefined | null;
  /** Filtro extra, p.ej. `{ categoria: 'Colectivo' }`. */
  filtroExtra?: () => Record<string, unknown>;
}

// El matcher por defecto de Material espera a touched (blur) o al submit. Acá el
// error de identificación repetida se muestra apenas responde el validador,
// mientras el usuario sigue escribiendo, y también si el valor precargado al
// editar ya está repetido (sin dirty ni touched).
class IdentificacionErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: AbstractControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    if (!control) return false;
    if (control.errors?.['identificacionRepetida']) return true;
    return !!(
      control.invalid &&
      (control.dirty || control.touched || form?.submitted)
    );
  }
}

/**
 * Chequeo de identificación repetida para los formularios de alta/edición.
 *
 * Regla: repetida dentro del mismo cliente bloquea el guardado; repetida en
 * otro cliente del árbol accesible solo advierte. Se usa con
 * `app-mensaje-identificacion`, que muestra el estado.
 *
 * ```ts
 * public chequeo = new ChequeoIdentificacion<IActivo>({
 *   entidad: 'vehículo',
 *   articulo: 'un',
 *   campo: 'identificacion',
 *   control: () => this.form?.get('identificacion'),
 *   listar: (query) => this.service.getFiltered(query),
 *   idCliente: () => this.form?.get('idCliente')?.value,
 *   excluirId: () => this.id,
 *   filtroExtra: () => ({ categoria: 'Vehículo' }),
 * });
 * // en el form: new FormControl(valor, Validators.required, this.chequeo.validador)
 * ```
 */
export class ChequeoIdentificacion<T extends EntidadIdentificable> {
  public readonly matcher = new IdentificacionErrorStateMatcher();

  // Si la consulta al server falla no se afirma que esté disponible.
  private consultaFallo = false;

  // Último match encontrado. Si es del mismo cliente bloquea; si es de otro
  // cliente del árbol solo advierte.
  private match: { cliente: string; mismoCliente: boolean } | null = null;

  constructor(private opciones: OpcionesChequeoIdentificacion<T>) {}

  private get control(): AbstractControl | null | undefined {
    return this.opciones.control();
  }

  ///////////////////////////////////////////////////////////////////////////
  // ESTADO PARA LA VISTA

  public get verificando(): boolean {
    return !!this.control?.pending;
  }

  /** El campo quedó vacío después de haberlo tocado. */
  public get requerida(): boolean {
    if (!this.control?.errors?.['required']) return false;
    return this.control.dirty || this.control.touched;
  }

  /** Repetida dentro del mismo cliente: bloquea el guardado. */
  public get repetida(): boolean {
    return !!this.control?.errors?.['identificacionRepetida'];
  }

  /** Nombre del otro cliente del árbol que ya la usa (se permite guardar). */
  public get enOtroCliente(): string | null {
    if (!this.match || this.match.mismoCliente) return null;
    if (this.verificando) return null;
    return this.match.cliente;
  }

  /** Cargada, ya verificada contra el server y sin repetidos. */
  public get disponible(): boolean {
    if (!this.control || this.control.pending || this.control.invalid) {
      return false;
    }
    if (this.consultaFallo || this.match) return false;
    return !!(this.control.value ?? '').toString().trim();
  }

  public get textoRepetida(): string {
    const { articulo, entidad } = this.opciones;
    return `Este cliente ya tiene ${articulo} ${entidad} con esa identificación.`;
  }

  public get textoEnOtroCliente(): string {
    const { articulo, entidad } = this.opciones;
    return `AVISO: el cliente "${this.enOtroCliente}" ya tiene ${articulo} ${entidad} con esa identificación.`;
  }

  ///////////////////////////////////////////////////////////////////////////
  // VALIDACIÓN

  /**
   * Validador async del control. Angular cancela la suscripción anterior en
   * cada tecla, así que el timer alcanza como debounce.
   */
  public validador = (
    control: AbstractControl,
  ): Observable<ValidationErrors | null> => {
    const valor = (control.value ?? '').toString().trim();
    if (!valor) {
      this.match = null;
      return of(null);
    }
    return timer(300).pipe(
      switchMap(() => {
        this.consultaFallo = false;
        return from(this.buscar(valor));
      }),
      map((encontrada) => {
        this.match = this.armarMatch(encontrada);
        // Solo el repetido del propio cliente invalida el form. El de un
        // cliente hijo/hermano se permite y se avisa en naranja.
        return this.match?.mismoCliente
          ? { identificacionRepetida: true }
          : null;
      }),
      // Si falla la consulta no se bloquea el guardado; el backend decide.
      catchError(() => {
        this.consultaFallo = true;
        this.match = null;
        return of(null);
      }),
    );
  };

  /** Revalidar: el mensaje nombra al cliente, que puede haber cambiado. */
  public revalidar(): void {
    this.control?.updateValueAndValidity();
  }

  /**
   * Chequeo final contra el server antes de guardar: cubre el envío con el
   * validador async todavía pendiente y el alta concurrente de otro usuario.
   * Devuelve `false` (y marca el error) si hay duplicado del mismo cliente.
   */
  public async puedeGuardar(): Promise<boolean> {
    const valor = (this.control?.value ?? '').toString().trim();
    const encontrada = valor ? await this.buscar(valor) : null;
    this.match = this.armarMatch(encontrada);
    if (this.match?.mismoCliente) {
      this.control?.setErrors({ identificacionRepetida: true });
      return false;
    }
    return true;
  }

  ///////////////////////////////////////////////////////////////////////////

  /**
   * Busca otra entidad con la misma identificación (case-insensitive) dentro
   * del árbol de clientes accesible. Prioriza el match del propio cliente, que
   * es el único que bloquea el guardado.
   */
  private async buscar(identificacion: string): Promise<T | null> {
    const escapada = identificacion.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const filter = {
      ...(this.opciones.filtroExtra?.() ?? {}),
      [this.opciones.campo]: { $regex: `^${escapada}$`, $options: 'i' },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: `${this.opciones.campo} idCliente`,
      populate: JSON.stringify([{ path: 'cliente', select: 'nombre' }]),
      limit: 0,
      includeChildren: true,
    };
    const res = await this.opciones.listar(query);
    // Excluir la propia entidad cuando se está editando.
    const excluir = this.opciones.excluirId?.();
    const candidatas = (res.datos ?? []).filter((d) => d._id !== excluir);
    return (
      candidatas.find((d) => this.esMismoCliente(d)) ?? candidatas[0] ?? null
    );
  }

  private armarMatch(
    encontrada: T | null,
  ): { cliente: string; mismoCliente: boolean } | null {
    if (!encontrada) return null;
    return {
      cliente: encontrada.cliente?.nombre ?? 'otro cliente',
      mismoCliente: this.esMismoCliente(encontrada),
    };
  }

  private esMismoCliente(entidad: T): boolean {
    return String(entidad.idCliente) === String(this.opciones.idCliente());
  }
}
